import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Receipt, ExternalLink, RefreshCw, Mail } from "lucide-react";

function parseSheet(url) {
  if (!url) return null;
  const m = url.match(/spreadsheets\/d\/([^/]+)/);
  if (!m) return null;
  const gidMatch = url.match(/[?#&]gid=(\d+)/);
  return { id: m[1], gid: gidMatch ? gidMatch[1] : "" };
}

function toPreviewUrl(url) {
  const p = parseSheet(url);
  if (!p) return url || "";
  return `https://docs.google.com/spreadsheets/d/${p.id}/preview${p.gid ? `?gid=${p.gid}` : ""}`;
}

function toOpenUrl(url) {
  const p = parseSheet(url);
  if (!p) return url || "";
  return `https://docs.google.com/spreadsheets/d/${p.id}/edit${p.gid ? `?gid=${p.gid}#gid=${p.gid}` : ""}`;
}

export default function Abrechnung() {
  const [info, setInfo] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const token = localStorage.getItem("ebel_token");

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data));
  }, []);

  if (!info) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="abrechnung-page">
        <p className="text-center text-[#4A4F46]">Lade …</p>
      </div>
    );
  }

  const sheetUrl = info.abrechnung_sheet_url || "";
  const embedSrc = toPreviewUrl(sheetUrl);
  const openHref = toOpenUrl(sheetUrl);
  const imageId = info.abrechnung_image_id || "";

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="abrechnung-page">
      <div className="text-center mb-10">
        <p className="label-eyebrow mb-3">Übersicht &amp; Kosten</p>
        <h1 className="font-serif text-5xl lg:text-6xl tracking-tight" data-testid="abrechnung-title">
          {info.abrechnung_title || "Abrechnung 2026"}
        </h1>
        {info.abrechnung_intro && (
          <p className="text-[#4A4F46] mt-5 max-w-2xl mx-auto leading-relaxed whitespace-pre-line" data-testid="abrechnung-intro">
            {info.abrechnung_intro}
          </p>
        )}
      </div>

      {imageId && (
        <div className="bg-white border border-[#E5DFD3] rounded-2xl p-4 sm:p-6 mb-8" data-testid="abrechnung-image-card">
          <h2 className="font-serif text-2xl tracking-tight mb-4">Übersicht</h2>
          <img
            src={`${API}/photos/${imageId}/download?auth=${token}`}
            alt="Abrechnungs-Übersicht"
            className="w-full rounded-xl border border-[#E5DFD3]"
            data-testid="abrechnung-image"
          />
        </div>
      )}

      {sheetUrl ? (
        <div className="bg-white border border-[#E5DFD3] rounded-2xl p-4 sm:p-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2 text-[#4A4F46]">
              <Receipt className="w-5 h-5 text-[#48634A]" />
              <span className="text-sm">Die Tabelle dient nur zur Ansicht – Eintragungen macht der Admin.</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setReloadKey((k) => k + 1)}
                className="inline-flex items-center gap-1.5 text-xs text-[#4A4F46] hover:text-[#1A1C19] border border-[#E5DFD3] rounded-full px-3 py-1.5"
                data-testid="abrechnung-reload"
                title="Tabelle neu laden"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
              </button>
              <a
                href={openHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[#48634A] hover:text-[#364A38] border border-[#E5DFD3] hover:border-[#48634A] rounded-full px-3 py-1.5"
                data-testid="abrechnung-open"
              >
                <ExternalLink className="w-3.5 h-3.5" /> In neuem Tab öffnen
              </a>
            </div>
          </div>

          <div className="relative w-full overflow-hidden rounded-xl border border-[#E5DFD3] bg-white">
            <iframe
              key={reloadKey}
              src={embedSrc}
              title="Abrechnung 2026"
              className="w-full"
              style={{ height: "min(70vh, 700px)", border: 0 }}
              loading="lazy"
              data-testid="abrechnung-iframe"
            />
          </div>
        </div>
      ) : (
        <div className="text-center py-20 border border-dashed border-[#E5DFD3] rounded-2xl">
          <Receipt className="w-10 h-10 text-[#48634A] mx-auto mb-3" />
          <p className="font-serif text-2xl">Noch keine Tabelle hinterlegt</p>
          <p className="text-[#4A4F46] mt-2 text-sm">Der Admin kann die Sheet-URL im Verwaltungs-Bereich eintragen.</p>
        </div>
      )}

      <div className="mt-8 bg-[#F4EFE6] border border-[#E5DFD3] rounded-2xl p-5 flex items-center gap-3 justify-center text-center flex-wrap" data-testid="abrechnung-contact">
        <Mail className="w-5 h-5 text-[#48634A]" />
        <p className="text-[#4A4F46]">
          Wenn Fragen zur Abrechnung, bitte direkt{" "}
          <a href="mailto:a.ebel@web.de" className="text-[#48634A] underline hover:text-[#364A38]" data-testid="abrechnung-mail-link">
            a.ebel@web.de
          </a>{" "}
          fragen.
        </p>
      </div>
    </div>
  );
}
